import { Sha256 } from '@aws-crypto/sha256-js';
import DOMPurify, {
	type Config,
	type DOMPurify as DOMPurifyInstance,
	type NodeHook,
	type UponSanitizeAttributeHook,
	type UponSanitizeElementHook,
} from 'dompurify';

const LOCAL_FRAGMENT_REFERENCE = /^#[^\s\\]+$/u;
const SAFE_EMBEDDED_IMAGE_URI = /^data:image\/(?:avif|gif|jpe?g|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/iu;
const CSS_IMPORT_RULE = /@import\b[^;]*(?:;|$)/giu;
const CSS_URL_FUNCTION = /url\(\s*(?:(['"])(.*?)\1|([^)]*))\s*\)/giu;
const CSS_URL_TOKEN = /\burl\s*\(/iu;
const CSS_UNSAFE_RESOURCE_FUNCTION = /\b(?:-webkit-)?(?:cross-fade|image|image-set|src)\s*\(/iu;
const SVG_SCOPE_ATTRIBUTE = 'data-svg-sanitizer-scope';
const SVG_LEADING_SCOPE_SELECTOR = /^\[\s*data-svg-sanitizer-scope\s*=\s*(?:"[^"]*"|'[^']*')\s*\]/iu;
const SVG_ROOT_PSEUDO_SELECTOR = /^:(?:root|scope)(?=$|[.#:\s>+~]|\[)/iu;
const SVG_ROOT_TYPE_SELECTOR = /^(?:svg|\*)(?=$|[.#:\s>+~]|\[)/iu;
const ANIMATION_PROPERTY = /^(?:-webkit-)?animation(?:-name)?$/iu;
const ANIMATION_KEYWORDS = new Set<string>([
	'alternate',
	'alternate-reverse',
	'always',
	'backwards',
	'both',
	'cubic-bezier',
	'ease',
	'ease-in',
	'ease-in-out',
	'ease-out',
	'forwards',
	'frames',
	'infinite',
	'initial',
	'linear',
	'none',
	'normal',
	'paused',
	'reverse',
	'revert',
	'revert-layer',
	'running',
	'step-end',
	'step-start',
	'steps',
	'unset',
]);

const SVG_SANITIZATION_CONFIG: Config = {
	USE_PROFILES: {
		svg: true,
		svgFilters: true,
	},
	ADD_TAGS: ['use'],
	FORBID_TAGS: ['feimage'],
};

/**
 * Checks whether a reference is a same-document fragment without imposing an
 * ASCII-only identifier grammar.
 *
 * @param value Reference value to validate.
 * @returns Whether the trimmed value is a fragment-only reference.
 */
function isLocalFragmentReference(value: string): boolean {
	return LOCAL_FRAGMENT_REFERENCE.test(value.trim());
}

/**
 * Rejects CSS values that contain escaped or external resource references.
 *
 * The allow-list intentionally supports only same-document fragment `url()`
 * references. CSS image functions such as `image-set()` can encode URLs as
 * strings without a literal `url(` token, so those resource functions are
 * rejected instead of being parsed as trusted CSS.
 *
 * @param value CSS value to inspect.
 * @returns Whether all CSS resource references satisfy the allow-list.
 */
function hasSafeCssReferences(value: string): boolean {
	if (value.includes('\\')) {
		return false;
	}

	if (CSS_UNSAFE_RESOURCE_FUNCTION.test(value)) {
		return false;
	}

	if (!CSS_URL_TOKEN.test(value)) {
		return true;
	}

	const references = Array.from(value.matchAll(CSS_URL_FUNCTION));

	return (
		references.length > 0
		&& references.every((matches) => isLocalFragmentReference(
			matches[2] ?? matches[3] ?? '',
		))
	);
}

/**
 * Applies the SVG reference policy to an attribute value.
 *
 * @param element {@link Element} carrying the attribute.
 * @param attributeName Lowercase attribute name.
 * @param attributeValue Attribute value to validate.
 * @returns Whether the attribute value is safe to retain.
 */
function hasSafeReference(
	element: Element,
	attributeName: string,
	attributeValue: string,
): boolean {
	const tagName = element.nodeName.toLowerCase();

	if (
		attributeName === 'href'
		|| attributeName === 'xlink:href'
	) {
		return (
			isLocalFragmentReference(attributeValue)
			|| (
				tagName === 'image'
				&& SAFE_EMBEDDED_IMAGE_URI.test(attributeValue.trim())
			)
		);
	}

	return hasSafeCssReferences(attributeValue);
}

/**
 * Replaces external CSS URLs with inert values before CSSOM parsing.
 *
 * @param cssText Stylesheet or declaration text to rewrite.
 * @returns CSS text with only local fragment URLs preserved.
 */
function replaceUnsafeCssReferences(cssText: string): string {
	return cssText.replace(
		CSS_URL_FUNCTION,
		(
			reference,
			_quote,
			quotedReference,
			unquotedReference,
		) => (
			isLocalFragmentReference(quotedReference ?? unquotedReference ?? '')
				? reference
				: 'none'
		),
	);
}

/**
 * Derives a stable scope from fully sanitized, scope-neutral SVG markup.
 *
 * @param canonicalSVG Sanitized SVG using the fixed internal scope token.
 * @returns Scope token safe for use in an HTML data attribute and CSS name.
 */
function createScopeToken(canonicalSVG: string): string {
	const hash = new Sha256();
	hash.update(canonicalSVG);
	const digest = Array.from(hash.digestSync())
		.map((byte) => byte.toString(16).padStart(
			2,
			'0',
		))
		.join('');

	return `svg-sanitizer-${digest}`;
}

/**
 * Splits a CSS selector list without treating commas inside CSS syntax as
 * selector separators.
 *
 * @param selectorText Selector list emitted by the CSSOM.
 * @returns Individual top-level selectors.
 */
function splitCssSelectorList(selectorText: string): string[] {
	const selectors: string[] = [];
	let start = 0;
	let nestingDepth = 0;
	let quote: string | undefined;

	for (let index = 0; index < selectorText.length; index += 1) {
		const character = selectorText[index];

		if (quote) {
			if (character === quote) {
				quote = undefined;
			}

			continue;
		}

		if (character === '"' || character === "'") {
			quote = character;
		} else if (character === '(' || character === '[') {
			nestingDepth += 1;
		} else if (character === ')' || character === ']') {
			nestingDepth = Math.max(
				0,
				nestingDepth - 1,
			);
		} else if (character === ',' && nestingDepth === 0) {
			selectors.push(
				selectorText
					.slice(
						start,
						index,
					)
					.trim(),
			);
			start = index + 1;
		}
	}

	selectors.push(selectorText.slice(start).trim());

	return selectors.filter(Boolean);
}

/**
 * Rewrites root pseudo-selectors to the sanitized SVG root selector.
 *
 * @param selectorText Selector text to normalize.
 * @param scopeSelector Attribute selector identifying the SVG root.
 * @returns Selector text that targets the sanitized SVG root.
 */
function replaceSVGRootPseudoSelector(
	selectorText: string,
	scopeSelector: string,
): string {
	return selectorText.replace(
		SVG_ROOT_PSEUDO_SELECTOR,
		scopeSelector,
	);
}

/**
 * Detects a selector that begins with a sibling combinator.
 *
 * @param selectorText Selector text to inspect.
 * @returns Whether the selector starts with `+` or `~`.
 */
function startsWithSiblingCombinator(selectorText: string): boolean {
	const normalizedSelector = selectorText.trim();

	return (
		normalizedSelector.startsWith('+')
		|| normalizedSelector.startsWith('~')
	);
}

/**
 * Detects a sibling combinator that could escape the scoped SVG root.
 *
 * @param selectorText Selector text to inspect.
 * @returns Whether the first selector combinator can select a root sibling.
 */
function hasRootSiblingCombinator(selectorText: string): boolean {
	let nestingDepth = 0;
	let quote: string | undefined;

	for (let index = 0; index < selectorText.length; index += 1) {
		const character = selectorText[index];

		if (quote) {
			if (character === quote) {
				quote = undefined;
			}

			continue;
		}

		if (character === '"' || character === "'") {
			quote = character;
		} else if (character === '(' || character === '[') {
			nestingDepth += 1;
		} else if (character === ')' || character === ']') {
			nestingDepth = Math.max(
				0,
				nestingDepth - 1,
			);
		} else if (nestingDepth === 0) {
			if (character === '+' || character === '~') {
				return true;
			}

			if (character === '>' || character === '|') {
				return false;
			}

			if (/\s/u.test(character)) {
				let nextIndex = index + 1;

				while (
					nextIndex < selectorText.length
					&& /\s/u.test(selectorText[nextIndex])
				) {
					nextIndex += 1;
				}

				return (
					selectorText[nextIndex] === '+'
					|| selectorText[nextIndex] === '~'
				);
			}
		}
	}

	return false;
}

/**
 * Checks whether a selector's first compound can match the SVG root itself.
 *
 * @param selectorText Selector text to inspect.
 * @returns Whether root-specific selector scoping should be emitted.
 */
function canMatchSVGRoot(selectorText: string): boolean {
	const firstCharacter = selectorText[0];

	return (
		firstCharacter === '.'
		|| firstCharacter === '#'
		|| firstCharacter === '['
		|| firstCharacter === ':'
		|| SVG_ROOT_TYPE_SELECTOR.test(selectorText)
	);
}

/**
 * Prefixes selectors with scoped descendant/root forms, preserving their
 * relationship to the root when re-sanitizing an already scoped selector.
 *
 * @param selectorText Selector list to scope.
 * @param scopeSelector Attribute selector identifying the SVG root.
 * @returns Selector list constrained to that SVG subtree.
 */
function scopeCssSelector(
	selectorText: string,
	scopeSelector: string,
): string {
	const scopedSelectors = splitCssSelectorList(selectorText)
		.flatMap((selector) => {
			const priorScope = selector.match(SVG_LEADING_SCOPE_SELECTOR);

			if (priorScope) {
				/** Preserve root-only and descendant-only rules without trusting the old token. */
				if (hasRootSiblingCombinator(selector)) {
					return [];
				}

				return [
					selector.replace(
						SVG_LEADING_SCOPE_SELECTOR,
						scopeSelector,
					),
				];
			}

			if (startsWithSiblingCombinator(selector)) {
				return [];
			}

			const rootPseudoSelector = replaceSVGRootPseudoSelector(
				selector,
				scopeSelector,
			);

			if (rootPseudoSelector !== selector) {
				if (hasRootSiblingCombinator(selector)) {
					return [];
				}

				return [rootPseudoSelector];
			}

			const descendantSelector = `${scopeSelector} ${selector}`;
			/** The scope already identifies an SVG; `[scope]svg` / `[scope]*` would be invalid CSS. */
			const rootCompound = selector.replace(
				SVG_ROOT_TYPE_SELECTOR,
				'',
			);
			const rootSelector = (
				canMatchSVGRoot(selector)
				&& !hasRootSiblingCombinator(selector)
			)
				? `${scopeSelector}${rootCompound}`
				: undefined;

			return rootSelector
				? [
					rootSelector,
					descendantSelector,
				]
				: [descendantSelector];
		});

	return Array.from(new Set(scopedSelectors)).join(',');
}

/**
 * Collects keyframe names from a stylesheet and maps them to SVG-local names.
 *
 * @param rules {@link CSSRuleList} to walk.
 * @param scopeToken SVG scope token used in generated names.
 * @param animationNames {@link Map} to populate with original-to-scoped names.
 */
function collectAnimationNames(
	rules: CSSRuleList,
	scopeToken: string,
	animationNames: Map<string, string>,
): void {
	Array.from(rules).forEach((rule) => {
		if (rule.type === 7) {
			const keyframesRule = rule as CSSKeyframesRule;

			if (!animationNames.has(keyframesRule.name)) {
				animationNames.set(
					keyframesRule.name,
					`${scopeToken}-animation-${animationNames.size.toString(36)}`,
				);
			}
		} else if (rule.type === 4 || rule.type === 12) {
			collectAnimationNames(
				(rule as CSSGroupingRule).cssRules,
				scopeToken,
				animationNames,
			);
		}
	});
}

/**
 * Rewrites known animation names and rejects names that could resolve to host
 * document keyframes.
 *
 * @param value CSS animation declaration value.
 * @param animationNames {@link Map} of original-to-scoped keyframe names.
 * @returns Scoped animation value, or `undefined` when it is unsafe.
 */
function sanitizeAnimationValue(
	value: string,
	animationNames: Map<string, string>,
): string | undefined {
	const escapedNames = Array.from(animationNames.keys()).map((name) => name.replace(
		/[.*+?^${}()|[\]\\]/gu,
		'\\$&',
	));
	/** Replace original names only once, even when one matches a generated name. */
	const scopedValue = escapedNames.length
		? value.replace(
			new RegExp(
				`(^|[^\\p{L}\\p{N}_-])(${escapedNames.join('|')})(?=$|[^\\p{L}\\p{N}_-])`,
				'gu',
			),
			(_match, prefix: string, name: string) => `${prefix}${animationNames.get(name)}`,
		)
		: value;

	const scopedNames = new Set(animationNames.values());
	const identifiers = Array.from(
		scopedValue.matchAll(
			/(?:^|[^\p{L}\p{N}_-])([\p{L}_][\p{L}\p{N}_-]*)/gu,
		),
	).map((matches) => matches[1]);
	const hasUnscopedName = identifiers.some((identifier) => (
		!scopedNames.has(identifier)
		&& !ANIMATION_KEYWORDS.has(identifier.toLowerCase())
	));

	return hasUnscopedName ? undefined : scopedValue;
}

/**
 * Builds a detached CSSOM and falls back to a sandboxed iframe where needed.
 *
 * @param cssText CSS text to parse.
 * @param ownerDocument {@link Document} whose implementation creates the scratch DOM.
 * @returns Parsed {@link CSSRuleList} and an optional cleanup callback.
 */
function createCSSRules(
	cssText: string,
	ownerDocument: Document,
): {
	rules: CSSRuleList | undefined;
	cleanup: (() => void) | undefined;
} {
	const scratchDocument = ownerDocument.implementation.createHTMLDocument(
		'svg-sanitizer',
	);
	const styleElement = scratchDocument.createElement('style');
	styleElement.textContent = cssText;
	scratchDocument.head.append(styleElement);

	let rules = styleElement.sheet?.cssRules;
	let cleanup: (() => void) | undefined;

	if (!rules && typeof window !== 'undefined') {
		const iframe = window.document.createElement('iframe');
		iframe.setAttribute(
			'sandbox',
			'',
		);
		window.document.body?.append(iframe);
		const iframeDocument = iframe.contentDocument;

		if (iframeDocument) {
			const iframeStyleElement = iframeDocument.createElement('style');
			iframeStyleElement.textContent = cssText;
			iframeDocument.head.append(iframeStyleElement);
			rules = iframeStyleElement.sheet?.cssRules;
			cleanup = () => iframe.remove();
		} else {
			iframe.remove();
		}
	}

	return {
		rules,
		cleanup,
	};
}

/**
 * Removes unsafe URL and animation references from a live declaration list.
 *
 * @param declarations {@link CSSStyleDeclaration} to mutate and serialize.
 * @param animationNames {@link Map} of original-to-scoped keyframe names.
 * @returns Sanitized serialized declaration text.
 */
function sanitizeCSSDeclarationList(
	declarations: CSSStyleDeclaration,
	animationNames: Map<string, string>,
): string {
	for (let index = declarations.length - 1; index >= 0; index -= 1) {
		const propertyName = declarations.item(index);
		const propertyValue = declarations.getPropertyValue(propertyName);

		if (!hasSafeCssReferences(propertyValue)) {
			declarations.removeProperty(propertyName);
		} else if (ANIMATION_PROPERTY.test(propertyName)) {
			const sanitizedAnimation = sanitizeAnimationValue(
				propertyValue,
				animationNames,
			);

			if (sanitizedAnimation === undefined) {
				declarations.removeProperty(propertyName);
			} else {
				declarations.setProperty(
					propertyName,
					sanitizedAnimation,
					declarations.getPropertyPriority(propertyName),
				);
			}
		}
	}

	return declarations.cssText;
}

/**
 * Sanitizes inline CSS declarations through the detached CSSOM policy.
 *
 * @param cssText Declaration text to sanitize.
 * @param ownerDocument {@link Document} used to create the detached CSSOM.
 * @param animationNames {@link Map} of original-to-scoped keyframe names.
 * @returns Sanitized declaration text, or an empty string when unsafe.
 */
function sanitizeCSSDeclarations(
	cssText: string,
	ownerDocument: Document,
	animationNames: Map<string, string>,
): string {
	if (cssText.includes('\\')) {
		return '';
	}

	const { rules, cleanup } = createCSSRules(
		`.svg-sanitizer{${replaceUnsafeCssReferences(cssText)}}`,
		ownerDocument,
	);

	try {
		const styleRule = rules?.[0];

		if (!styleRule || styleRule.type !== 1) {
			return '';
		}

		return sanitizeCSSDeclarationList(
			(styleRule as CSSStyleRule).style,
			animationNames,
		);
	} finally {
		cleanup?.();
	}
}

/**
 * Serializes supported CSS rules after scoping selectors and keyframes.
 *
 * @param rule {@link CSSRule} to sanitize.
 * @param scopeSelector Attribute selector identifying the SVG root.
 * @param animationNames {@link Map} of original-to-scoped keyframe names.
 * @returns Sanitized serialized CSS rule, or an empty string when unsupported.
 */
function sanitizeCSSRule(
	rule: CSSRule,
	scopeSelector: string,
	animationNames: Map<string, string>,
): string {
	if (rule.type === 1) {
		const styleRule = rule as CSSStyleRule;
		const declarations = sanitizeCSSDeclarationList(
			styleRule.style,
			animationNames,
		);
		const scopedSelector = scopeCssSelector(
			styleRule.selectorText,
			scopeSelector,
		);

		return declarations && scopedSelector
			? `${scopedSelector}{${declarations}}`
			: '';
	}

	if (
		rule.type === 4
		|| rule.type === 12
	) {
		const groupingRule = rule as CSSGroupingRule;
		const nestedRules = Array.from(groupingRule.cssRules)
			.map((nestedRule) => sanitizeCSSRule(
				nestedRule,
				scopeSelector,
				animationNames,
			))
			.join('');

		if (!nestedRules) {
			return '';
		}

		const condition = (
			rule.type === 4
				? (rule as CSSMediaRule).conditionText
				: (rule as CSSSupportsRule).conditionText
		);
		const atRule = rule.type === 4 ? '@media' : '@supports';

		return `${atRule} ${condition}{${nestedRules}}`;
	}

	if (rule.type === 7) {
		const keyframesRule = rule as CSSKeyframesRule;
		const keyframes = Array.from(keyframesRule.cssRules)
			.map((keyframeRule) => {
				const keyframe = keyframeRule as CSSKeyframeRule;
				const declarations = sanitizeCSSDeclarationList(
					keyframe.style,
					animationNames,
				);

				return declarations
					? `${keyframe.keyText}{${declarations}}`
					: '';
			})
			.join('');

		const scopedName = animationNames.get(keyframesRule.name);

		return keyframes && scopedName
			? `@keyframes ${scopedName}{${keyframes}}`
			: '';
	}

	return '';
}

/**
 * Sanitizes an SVG stylesheet, including selector and keyframe scoping.
 *
 * @param cssText Stylesheet text to sanitize.
 * @param ownerDocument {@link Document} used to create the detached CSSOM.
 * @param scopeToken Unique token identifying the SVG subtree.
 * @param animationNames {@link Map} to populate with original-to-scoped keyframe names.
 * @returns Sanitized serialized stylesheet text.
 */
function sanitizeCSSStylesheet(
	cssText: string,
	ownerDocument: Document,
	scopeToken: string,
	animationNames: Map<string, string>,
): string {
	if (cssText.includes('\\')) {
		return '';
	}

	const safeCssText = replaceUnsafeCssReferences(
		cssText.replace(
			CSS_IMPORT_RULE,
			'',
		),
	);
	const { rules, cleanup } = createCSSRules(
		safeCssText,
		ownerDocument,
	);
	const scopeSelector = `[${SVG_SCOPE_ATTRIBUTE}="${scopeToken}"]`;

	try {
		if (rules) {
			collectAnimationNames(
				rules,
				scopeToken,
				animationNames,
			);
		}

		return rules
			? Array.from(rules)
				.map((rule) => sanitizeCSSRule(
					rule,
					scopeSelector,
					animationNames,
				))
				.join('')
			: '';
	} finally {
		cleanup?.();
	}
}

/**
 * Creates the hook that rewrites or removes SVG `<style>` elements.
 *
 * @param scopeToken Unique token identifying the SVG subtree.
 * @param animationNames {@link Map} of original-to-scoped keyframe names.
 * @returns {@link NodeHook} for stylesheet sanitization.
 */
function createSanitizeSVGStyleElement(
	scopeToken: string,
	animationNames: Map<string, string>,
): NodeHook {
	return (currentNode) => {
		if (
			currentNode.nodeType !== 1
			|| currentNode.nodeName.toLowerCase() !== 'style'
		) {
			return;
		}

		const element = currentNode as Element;
		const sanitizedStylesheet = sanitizeCSSStylesheet(
			element.textContent ?? '',
			element.ownerDocument,
			scopeToken,
			animationNames,
		);

		if (sanitizedStylesheet) {
			element.textContent = sanitizedStylesheet;
		} else {
			element.remove();
		}
	};
}

/**
 * Creates the hook that scopes SVG roots and permits only local `<use>` links.
 *
 * @param scopeToken Unique token identifying the SVG subtree.
 * @returns {@link UponSanitizeElementHook} for SVG root and `<use>` handling.
 */
function createSanitizeSVGElement(
	scopeToken: string,
): UponSanitizeElementHook {
	return (currentNode, hookEvent) => {
		if (currentNode.nodeType !== 1) {
			return;
		}

		const element = currentNode as Element;
		const tagName = hookEvent.tagName.toLowerCase();

		if (tagName === 'svg') {
			element.setAttribute(
				SVG_SCOPE_ATTRIBUTE,
				scopeToken,
			);
		}

		if (tagName !== 'use') {
			return;
		}

		const hasLocalReference = [
			element.getAttribute('href'),
			element.getAttribute('xlink:href'),
		].some((value) => value !== null && isLocalFragmentReference(value));

		if (!hasLocalReference) {
			element.remove();
		}
	};
}

/**
 * Creates the hook that sanitizes CSS-bearing SVG attributes and references.
 *
 * @param animationNames {@link Map} of original-to-scoped keyframe names.
 * @returns {@link UponSanitizeAttributeHook} for SVG reference sanitization.
 */
function createSanitizeSVGAttribute(
	animationNames: Map<string, string>,
): UponSanitizeAttributeHook {
	return (element, hookEvent) => {
		const attributeName = hookEvent.attrName.toLowerCase();

		if (attributeName === 'style') {
			const sanitizedStyle = sanitizeCSSDeclarations(
				hookEvent.attrValue,
				element.ownerDocument,
				animationNames,
			);

			if (sanitizedStyle) {
				hookEvent.attrValue = sanitizedStyle;
			} else {
				hookEvent.keepAttr = false;
			}

			return;
		}

		if (
			attributeName === 'href'
			|| attributeName === 'xlink:href'
			|| CSS_URL_TOKEN.test(hookEvent.attrValue)
			|| hookEvent.attrValue.includes('\\')
		) {
			hookEvent.keepAttr = hasSafeReference(
				element,
				attributeName,
				hookEvent.attrValue,
			);
		}
	};
}

/**
 * Selects the DOMPurify instance for the current browser or server runtime.
 *
 * @returns Supported {@link DOMPurifyInstance}, or `undefined` when unavailable.
 */
function getSanitizer(): DOMPurifyInstance | undefined {
	const sanitizer = (
		typeof window !== 'undefined'
			? DOMPurify(window)
			: DOMPurify.isSupported
				? DOMPurify
				: undefined
	);

	return sanitizer?.isSupported ? sanitizer : undefined;
}

/**
 * Applies the complete SVG policy with a specified scope and isolated hooks.
 *
 * @param svgContent SVG markup to sanitize, including any prior sanitizer output.
 * @param sanitizer Supported {@link DOMPurifyInstance} for this operation.
 * @param scopeToken Scope used for root attributes, selectors, and animations.
 * @returns XML-safe sanitized SVG markup suitable for storage and rendering.
 */
function sanitizeSVGWithScope(
	svgContent: string,
	sanitizer: DOMPurifyInstance,
	scopeToken: string,
): string {
	const animationNames = new Map<string, string>();
	const sanitizeSVGElement = createSanitizeSVGElement(scopeToken);
	const sanitizeSVGStyleElement = createSanitizeSVGStyleElement(
		scopeToken,
		animationNames,
	);
	const sanitizeSVGAttribute = createSanitizeSVGAttribute(animationNames);

	sanitizer.addHook(
		'uponSanitizeElement',
		sanitizeSVGElement,
	);
	sanitizer.addHook(
		'afterSanitizeElements',
		sanitizeSVGStyleElement,
	);
	sanitizer.addHook(
		'uponSanitizeAttribute',
		sanitizeSVGAttribute,
	);

	try {
		const sanitizedFragment = sanitizer.sanitize(
			svgContent,
			{
				...SVG_SANITIZATION_CONFIG,
				RETURN_DOM_FRAGMENT: true,
			},
		);

		return new XMLSerializer().serializeToString(sanitizedFragment);
	} finally {
		sanitizer.removeHook(
			'uponSanitizeElement',
			sanitizeSVGElement,
		);
		sanitizer.removeHook(
			'afterSanitizeElements',
			sanitizeSVGStyleElement,
		);
		sanitizer.removeHook(
			'uponSanitizeAttribute',
			sanitizeSVGAttribute,
		);
	}
}

/**
 * Removes unsafe SVG markup with content-derived scopes so sanitizing unchanged
 * artwork again does not invalidate the editor's cached raster image.
 *
 * @param svgContent Raw SVG markup received from an uploaded or remote logo.
 * @returns Stable, XML-safe sanitized SVG markup suitable for storage and rendering.
 * @throws If the current runtime does not expose a supported DOM sanitizer.
 */
export default function sanitizeSVG(svgContent: string): string {
	const sanitizer = getSanitizer();

	if (!sanitizer) {
		throw new Error('SVG sanitization requires a supported DOM environment');
	}

	/** Never trust incoming scope markers or hash them into the next scope. */
	const canonicalSVG = sanitizeSVGWithScope(
		svgContent,
		sanitizer,
		'svg-sanitizer',
	);

	return sanitizeSVGWithScope(
		canonicalSVG,
		sanitizer,
		createScopeToken(canonicalSVG),
	);
}
